/* eslint-disable react/no-unescaped-entities */
import React from "react";
import Link from "next/link";
import { APPS, BHLogoMark } from "@buddieshr/components/brandLogos";

// ─────────────────────────────────────────────────────────────
// BuddiesHR Platform landing pages (/slack & /microsoft-teams)
// Shared sections, parameterized by PLATFORMS config.
// Reuses APPS + mascots from brand/logos.jsx and global sections
// (LogoMarquee, Modular, NativeNotBot, Testimonials, FooterCTA)
// from components/landing-sections.jsx.
// ─────────────────────────────────────────────────────────────

export const PLATFORMS = {
  slack: {
    id: 'slack',
    name: 'Slack',
    logo: '/img/redesign/slack-logo.png',
    url: 'acme-hq.slack.com',
    accent: '#4A154B',
    accentSoft: 'rgba(74,21,75,.07)',
    cta: 'Add to Slack free',
    award: 'Featured by Slack',
    heroSub: <>Kudos, pulse surveys, birthdays, coffee chats, reviews delivered as native Slack messages your team actually responds to. <strong style={{ color: 'var(--bh-ink)' }}>Not another portal nobody logs into.</strong></>,
    otherId: 'teams',
    otherName: 'Microsoft Teams',
    otherHref: '/microsoft-teams',
    steps: [
      { num: '1', time: '≈ 60 sec', title: 'Click “Add to Slack”', body: 'Standard Slack OAuth no IT ticket, no CSV import, no kickoff call. You pick the apps, Slack asks you to approve, done.' },
      { num: '2', time: '≈ 2 min', title: 'Pick your channels', body: 'Tell Clappy where kudos live, Billy where to celebrate birthdays, Pulsy who to survey. The defaults work fine too.' },
      { num: '3', time: '0 training', title: 'Your team takes it from there', body: 'The first kudos prompt and pulse check go out on their own. Nobody needs onboarding it’s just Slack messages.' },
    ],
    features: [
      { emoji: '👏', surface: 'Channel', visual: 'kudos', title: 'Kudos where everyone sees them', body: 'Clappy posts recognition in the channel you choose, tied to your company values. The whole team piles on with reactions.' },
      { emoji: '🐙', surface: 'DM', visual: 'pulse', title: 'Pulse checks as DMs', body: 'Pulsy asks privately one minute, fully anonymous, answered without leaving Slack. That’s how you get 80%+ response rates.' },
      { emoji: '⚡', surface: 'Slash command', visual: 'slash', title: 'Give kudos without leaving Slack', body: 'Type /give and Clappy opens a quick form right in Slack: pick the person, a company value, and a note. Submit and the card posts itself.' },
      { emoji: '🌴', surface: 'Slash command', visual: 'palmy', title: 'Time off without the back-and-forth', body: 'Palmy lets employees request vacation and check their balance right in Slack. Managers approve in one click no spreadsheet, no email thread.' },
      { emoji: '☕️', surface: 'Group DM', visual: 'coffee', title: 'Coffee chats that schedule themselves', body: 'Alfy pairs people across teams, opens a group DM, and nudges them to find a time. Remote teams’ favorite feature.' },
      { emoji: '🔕', surface: 'Notifications', visual: 'quiet', title: 'Respects how Slack works', body: 'Time zones, quiet hours, do-not-disturb. BuddiesHR never spams a channel or pings someone at 3am.' },
    ],
    faq: [
      { q: 'What permissions does it need?', a: 'Only the scopes each app needs to post its own messages and read reactions to them. BuddiesHR never reads your channel history or DMs. The full scope list is public on our security page.' },
      { q: 'Do employees need new accounts?', a: 'No. Everyone participates with their existing Slack identity. There is nothing to sign up for, install individually, or remember a password for.' },
      { q: 'Does it work in private channels?', a: 'Yes invite the app to any private channel and it behaves exactly as it does in public ones. It only sees the channels it’s invited to.' },
      { q: 'What does it cost?', a: '$1/user/month for any single app, $2 for the full suite, $3 with SimplePerf included. 30-day free trial, free forever under 10 employees, public pricing on every page.' },
    ],
  },
  teams: {
    id: 'teams',
    name: 'Microsoft Teams',
    logo: '/img/redesign/teams-logo.png',
    url: 'teams.microsoft.com',
    accent: '#5B5FC7',
    accentSoft: 'rgba(91,95,199,.08)',
    cta: 'Add to Teams free',
    award: 'Top Rated App · Teams store',
    heroSub: <>Kudos, pulse surveys, birthdays, coffee chats, reviews posted right in your Teams channels and chats. <strong style={{ color: 'var(--bh-ink)' }}>Not another portal nobody logs into.</strong></>,
    otherId: 'slack',
    otherName: 'Slack',
    otherHref: '/',
    steps: [
      { num: '1', time: '≈ 2 min', title: 'Install from the Teams store', body: 'Find BuddiesHR in the Microsoft Teams app store. If your org requires admin consent, we have a one-page guide for IT most approvals take minutes, not weeks.' },
      { num: '2', time: '≈ 2 min', title: 'Pick your teams & channels', body: 'Tell Clappy where kudos live, Billy where to celebrate birthdays, Pulsy who to survey. The defaults work fine too.' },
      { num: '3', time: '0 training', title: 'Your team takes it from there', body: 'The first kudos prompt and pulse check go out on their own on desktop, web, and the Teams mobile app. Nobody needs onboarding.' },
    ],
    features: [
      { emoji: '🎂', surface: 'Channel', visual: 'billy', title: 'Birthdays & anniversaries, never missed', body: 'Billy celebrates every birthday and work anniversary right in your Teams channel, so the whole team can pile on with wishes.' },
      { emoji: '👏', surface: 'Channel', visual: 'kudos', soon: true, title: 'Kudos where everyone sees them', body: 'Clappy posts recognition cards in the channel you choose, tied to your company values. The whole team piles on with reactions.' },
      { emoji: '🐙', surface: 'Chat', visual: 'pulse', soon: true, title: 'Pulse checks in private chat', body: 'Pulsy asks privately one minute, fully anonymous, answered without leaving Teams. That’s how you get 80%+ response rates.' },
      { emoji: '☕️', surface: 'Group chat', visual: 'coffee', title: 'Coffee chats that schedule themselves', body: 'Alfy pairs people across departments, opens a group chat, and nudges them to find a time. Remote teams’ favorite feature.' },
      { emoji: '📱', surface: 'iOS & Android', visual: 'mobile', title: 'Works on Teams mobile', body: 'Frontline or on the road everything works in the Teams mobile app, with nothing extra to install.' },
      { emoji: '🔐', surface: 'Microsoft 365', visual: 'sso', title: 'Sign-in handled by Microsoft', body: 'Everyone participates with their existing Microsoft 365 identity. No new accounts, no passwords, no user provisioning.' },
    ],
    faq: [
      { q: 'Does IT need to approve it?', a: 'That depends on your org’s app policies. If admin consent is required, we provide a one-page guide listing exactly what BuddiesHR requests and why most IT teams approve it in minutes.' },
      { q: 'What permissions does it need?', a: 'Only what each app needs to post its own messages and read reactions to them. BuddiesHR never reads your channel history or private chats. The full list is public on our security page.' },
      { q: 'Do employees need new accounts?', a: 'No. Everyone participates with their existing Microsoft 365 identity. There is nothing to sign up for or remember a password for.' },
      { q: 'Where is our data stored?', a: 'On EU-hosted infrastructure, GDPR-compliant, with export available anytime. BuddiesHR is bootstrapped and made in Paris your data is the product we protect, not the product we sell.' },
      { q: 'Part of the company is on Slack?', a: 'BuddiesHR is native in Slack too, and both connect to the same HR dashboard one program, one bill, two chat tools.' },
      { q: 'What does it cost?', a: '$1/user/month for any single app, $2 for the full suite, $3 with SimplePerf included. 30-day free trial, free forever under 10 employees, public pricing on every page.' },
    ],
  },
};

// Resolve a brand logo path to its bundled blob URL when running as a
// standalone file (window.__resources), else fall back to the raw path.
function pfAsset(path) {
  const map = { '/img/redesign/slack-logo.png': 'slackLogo', '/img/redesign/teams-logo.png': 'teamsLogo' };
  const id = map[path];
  return (typeof window !== 'undefined' && window.__resources && id && window.__resources[id]) || path;
}

// ═══ HERO ═══
export function PlatformHero({ p }) {
  return (
    <section className="bh-section-hero" data-screen-label={`${p.name} hero`} style={{ position: 'relative', overflow: 'hidden' }}>
      <div className="bh-blob" style={{
        position: 'absolute', top: -160, right: -180, width: 700, height: 700,
        background: `radial-gradient(circle at 40% 40%, var(--bh-sun-100), ${p.accentSoft} 55%, transparent 72%)`,
        zIndex: 0, filter: 'blur(2px)',
      }} />
      <div className="pf-hero-grid" style={{ position: 'relative', zIndex: 1, maxWidth: 1240, margin: '0 auto', display: 'grid', gridTemplateColumns: '1.05fr 1fr', gap: 56, alignItems: 'center' }}>
        <div>
          <div className="bh-chip" style={{ marginBottom: 22 }}>
            <img src={pfAsset(p.logo)} alt="" style={{ width: 15, height: 15 }} />
            BuddiesHR for {p.name}
          </div>
          <h1 style={{ fontSize: 'clamp(34px, 4.3vw, 58px)', lineHeight: 1.05, letterSpacing: '-0.035em' }}>
            The People Platform<br/>that lives in{' '}
            <span style={{
              background: 'linear-gradient(95deg, var(--bh-coral-400), var(--bh-sun-400) 60%, var(--bh-plum-400))',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              fontStyle: 'italic',
            }}>{p.name}.</span>
          </h1>
          <p style={{ fontSize: 19, lineHeight: 1.5, color: 'var(--bh-ink-soft)', marginTop: 22, maxWidth: 520 }}>
            {p.heroSub}
          </p>
          <div style={{ marginTop: 32, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Link href="/get-started" className="bh-btn primary" style={{ textDecoration: 'none' }}>
              <img src={pfAsset(p.logo)} alt="" style={{ width: 20, height: 20, marginRight: 8, verticalAlign: '-4px' }} />{p.cta} <span className="bh-arrow">→</span>
            </Link>
            <Link href="/demo" className="bh-btn ghost" style={{ textDecoration: 'none' }}>Book a 15-min demo</Link>
          </div>
          <div style={{ marginTop: 20, fontSize: 13, color: 'var(--bh-ink-mute)' }}>
            30-day free trial · Free forever under 10 employees · From $1/user/month
          </div>
          <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, color: 'var(--bh-ink-mute)', flexWrap: 'wrap' }}>
            <span>⭐ 4.8 on G2</span>
            <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--bh-line-strong)' }}></span>
            <span>🏅 {p.award}</span>
            <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--bh-line-strong)' }}></span>
            <span>1,000+ HR teams</span>
          </div>
        </div>
        <PlatformChatVisual p={p} />
      </div>
    </section>
  );
}

// ═══ CHAT WINDOW VISUAL generic chat surface skinned per platform ═══
function PlatformChatVisual({ p }) {
  const byId = Object.fromEntries(APPS.map(a => [a.id, a]));
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <PfFloatingMascot id="clappy" top="-7%" left="-7%" size={72} rot={-10} delay="0s" />
      <PfFloatingMascot id="pulsy" top="74%" right="-6%" size={66} rot={9} delay="1.2s" />
      <div style={{
        position: 'absolute', inset: '8% 6%', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,176,32,.20), transparent 65%)',
        filter: 'blur(28px)', zIndex: 0,
      }}></div>

      <div style={{
        position: 'relative', zIndex: 2,
        borderRadius: 18, background: '#fff',
        boxShadow: '0 30px 60px -20px rgba(31,27,22,.28), 0 10px 24px -8px rgba(31,27,22,.12), 0 0 0 1px rgba(31,27,22,.06)',
        overflow: 'hidden',
      }}>
        {/* window chrome */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#F4EFE7', borderBottom: '1px solid rgba(31,27,22,.08)' }}>
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#FF5F57' }}></span>
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#FEBC2E' }}></span>
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#28C840' }}></span>
          <div style={{ margin: '0 auto', fontSize: 11, color: 'var(--bh-ink-mute)', fontWeight: 500, padding: '3px 14px', background: '#fff', borderRadius: 6, border: '1px solid rgba(31,27,22,.06)' }}>
            {p.url}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: p.id === 'slack' ? '150px 1fr' : '56px 1fr', minHeight: 420 }}>
          {p.id === 'slack' ? <PfSlackSidebar /> : <PfTeamsRail byId={byId} />}

          <div style={{ display: 'flex', flexDirection: 'column', background: '#fff', minWidth: 0 }}>
            <div style={{ padding: '11px 16px', borderBottom: '1px solid rgba(31,27,22,.08)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--bh-ink)' }}>
                {p.id === 'slack' ? '# team-wins' : 'Team wins'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--bh-ink-mute)' }}>
                {p.id === 'slack' ? '· 58 members' : '· People & Culture · 58 members'}
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, padding: '3px 8px', background: 'var(--app-clappy-bg)', color: 'var(--bh-ink)', borderRadius: 999, fontWeight: 600 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--app-clappy)' }}></span>
                BuddiesHR active
              </div>
            </div>
            <div style={{ padding: '14px 16px 10px', display: 'flex', flexDirection: 'column', gap: 13, overflow: 'hidden' }}>
              <PfMessage app={byId.clappy} time="10:42 AM"
                header={<><b>Maya</b> gave kudos to <b>Jordan</b></>}
                card={{
                  title: '🎉 Shipped the new onboarding flow',
                  body: '“Jordan saved our launch debugging the SSO bug. Living the #craft value.”',
                  reacts: [['🙌', 12], ['❤️', 8]],
                }} />
              <PfMessage app={byId.pulsy} time="10:44 AM"
                header={<>Tuesday pulse <b>1 min, anonymous</b></>}
                card={{
                  title: 'How’s this week going?',
                  poll: [['😍 Great', 62], ['🙂 Fine', 28], ['😕 Rough', 10]],
                }} />
              <PfMessage app={byId.alfy} time="10:51 AM"
                header={<>New coffee chat pairing ☕️</>}
                card={{
                  title: 'Maya × Priya this week',
                  body: p.id === 'slack' ? 'I opened a group DM for you two. Friday 11am look good?' : 'I opened a group chat for you two. Friday 11am look good?',
                  reacts: [['☕️', 2]],
                }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PfSlackSidebar() {
  const item = (label, opts = {}) => (
    <div style={{
      padding: '3px 8px', borderRadius: 5, fontSize: 12,
      background: opts.active ? 'rgba(255,255,255,.14)' : 'transparent',
      color: opts.active || opts.unread ? '#fff' : 'rgba(255,255,255,.72)',
      fontWeight: opts.active || opts.unread ? 700 : 500,
      display: 'flex', alignItems: 'center', gap: 6,
    }}>
      {opts.bot && <span style={{ fontSize: 8, padding: '1px 4px', background: 'rgba(255,255,255,.15)', borderRadius: 3, fontWeight: 700 }}>APP</span>}
      {opts.hash && <span style={{ opacity: .7 }}>#</span>}
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      {opts.unread && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#E85D75', flexShrink: 0 }}></span>}
    </div>
  );
  return (
    <aside style={{ background: '#3F0E40', color: '#fff', padding: '12px 0', fontSize: 12 }}>
      <div style={{ padding: '0 12px 9px', borderBottom: '1px solid rgba(255,255,255,.1)', marginBottom: 9 }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>Acme Inc</div>
      </div>
      <div style={{ padding: '0 8px', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,.55)', fontWeight: 700, padding: '6px 8px 4px', letterSpacing: '.03em' }}>BuddiesHR ▾</div>
        {item('clappy', { bot: true, unread: true })}
        {item('pulsy', { bot: true })}
        {item('alfy', { bot: true, unread: true })}
        {item('billy', { bot: true })}
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,.55)', fontWeight: 700, padding: '12px 8px 4px', letterSpacing: '.03em' }}>Channels</div>
        {item('general', { hash: true })}
        {item('random', { hash: true })}
        {item('team-wins', { hash: true, active: true })}
      </div>
    </aside>
  );
}

function PfTeamsRail({ byId }) {
  const Clappy = byId.clappy.Logo;
  const rail = [
    ['🔔', 'Activity'], ['💬', 'Chat'], ['👥', 'Teams'], ['📅', 'Calendar'],
  ];
  return (
    <aside style={{ background: '#EBEBF4', borderRight: '1px solid rgba(31,27,22,.07)', padding: '12px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      {rail.map(([glyph, label]) => (
        <div key={label} title={label} style={{
          width: 38, height: 38, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, color: '#616161', background: 'transparent',
        }}>{glyph}</div>
      ))}
      <div style={{
        width: 38, height: 38, borderRadius: 9, background: '#fff',
        border: '1.5px solid #5B5FC7', boxShadow: '0 2px 6px rgba(91,95,199,.25)',
        padding: 4, marginTop: 4,
      }}>
        <Clappy />
      </div>
    </aside>
  );
}

function PfMessage({ app, time, header, card }) {
  const Logo = app.Logo;
  return (
    <div style={{ display: 'flex', gap: 9 }}>
      <div style={{ width: 32, height: 32, flexShrink: 0, borderRadius: 8, overflow: 'hidden', background: app.bg }}>
        <Logo />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 2 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--bh-ink)' }}>{app.name}</span>
          <span style={{ fontSize: 8.5, padding: '1px 4px', background: 'rgba(31,27,22,.08)', color: 'var(--bh-ink-mute)', borderRadius: 3, fontWeight: 700, letterSpacing: '.04em' }}>APP</span>
          <span style={{ fontSize: 10.5, color: 'var(--bh-ink-mute)' }}>{time}</span>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--bh-ink-soft)', marginBottom: 5, lineHeight: 1.4 }}>{header}</div>
        <div style={{ borderLeft: `3px solid ${app.color}`, background: app.bg, borderRadius: '0 8px 8px 0', padding: '9px 11px' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--bh-ink)' }}>{card.title}</div>
          {card.body && <div style={{ fontSize: 11.5, color: 'var(--bh-ink-soft)', lineHeight: 1.45, marginTop: 3 }}>{card.body}</div>}
          {card.poll && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
              {card.poll.map(([label, pct]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 10.5, width: 60, color: 'var(--bh-ink)', flexShrink: 0 }}>{label}</div>
                  <div style={{ flex: 1, height: 6, background: 'rgba(31,27,22,.08)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: app.color, borderRadius: 4 }}></div>
                  </div>
                  <div style={{ fontSize: 9.5, width: 26, textAlign: 'right', color: 'var(--bh-ink-mute)', fontWeight: 600 }}>{pct}%</div>
                </div>
              ))}
            </div>
          )}
        </div>
        {card.reacts && (
          <div style={{ display: 'flex', gap: 4, marginTop: 5 }}>
            {card.reacts.map(([e, n]) => (
              <div key={e} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10.5, padding: '2px 7px', background: '#fff', border: '1px solid rgba(31,27,22,.1)', borderRadius: 12, color: 'var(--bh-ink)', fontWeight: 600 }}>
                <span>{e}</span><span>{n}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PfFloatingMascot({ id, top, left, right, size, rot = 0, delay = '0s' }) {
  const app = APPS.find(a => a.id === id);
  if (!app) return null;
  const Logo = app.Logo;
  return (
    <div className="bh-float" style={{
      position: 'absolute', top, left, right, width: size, height: size,
      transform: `rotate(${rot}deg)`, zIndex: 3, animationDelay: delay,
      filter: 'drop-shadow(0 12px 20px rgba(31,27,22,.22))',
    }}>
      <Logo />
    </div>
  );
}

// ═══ INSTALL STEP VISUALS mini UI vignettes per step ═══
function PfStepFrame({ children }) {
  return (
    <div style={{
      height: 168, margin: '18px 0 20px',
      background: 'var(--bh-cream)', border: '1px solid var(--bh-line)',
      borderRadius: 'var(--bh-r-md)', position: 'relative', overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {children}
    </div>
  );
}

// Step 1 the consent / install dialog
function PfVisualInstall({ p }) {
  return (
    <PfStepFrame>
      <div style={{
        width: 210, background: '#fff', borderRadius: 12,
        border: '1px solid var(--bh-line)', boxShadow: 'var(--bh-shadow-md)',
        padding: '14px 16px', textAlign: 'center', position: 'relative',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 9 }}>
          <BHLogoMark size={28} />
          <span style={{ fontSize: 13, color: 'var(--bh-ink-mute)', fontWeight: 600 }}>+</span>
          <img src={pfAsset(p.logo)} alt="" style={{ width: 28, height: 28 }} />
        </div>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--bh-ink)' }}>
          {p.id === 'slack' ? 'BuddiesHR wants to join Acme Inc' : 'Add BuddiesHR to your org'}
        </div>
        <div style={{ fontSize: 9.5, color: 'var(--bh-ink-mute)', marginTop: 3, lineHeight: 1.4 }}>
          Posts its own messages. Never reads history.
        </div>
        <div style={{
          marginTop: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--bh-ink)', color: 'var(--bh-cream)',
          fontSize: 11, fontWeight: 700, padding: '6px 22px', borderRadius: 999,
        }}>{p.id === 'slack' ? 'Allow' : 'Add'}</div>
        <span style={{ position: 'absolute', bottom: 2, right: 48, fontSize: 18 }}>👆</span>
      </div>
    </PfStepFrame>
  );
}

// Step 2 app → channel mapping rows
function PfVisualChannels({ p }) {
  const byId = Object.fromEntries(APPS.map(a => [a.id, a]));
  const rows = p.id === 'slack'
    ? [['clappy', '# kudos'], ['billy', '# celebrations'], ['pulsy', 'DMs everyone']]
    : [['clappy', 'Kudos'], ['billy', 'Celebrations'], ['pulsy', 'Private chat']];
  return (
    <PfStepFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, width: 212 }}>
        {rows.map(([id, channel]) => {
          const app = byId[id];
          const Logo = app.Logo;
          return (
            <div key={id} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: '#fff', border: '1px solid var(--bh-line)',
              borderRadius: 9, padding: '6px 9px', boxShadow: 'var(--bh-shadow-sm)',
            }}>
              <div style={{ width: 24, height: 24, flexShrink: 0, borderRadius: 6, overflow: 'hidden', background: app.bg }}>
                <Logo />
              </div>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--bh-ink)' }}>{app.name}</span>
              <span style={{ fontSize: 11, color: 'var(--bh-ink-mute)' }}>→</span>
              <span style={{
                marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 10.5, fontWeight: 600, color: 'var(--bh-ink-soft)',
                background: 'var(--bh-cream)', border: '1px solid var(--bh-line-strong)',
                padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
              }}>{channel} <span style={{ fontSize: 8, color: 'var(--bh-ink-mute)' }}>▾</span></span>
            </div>
          );
        })}
      </div>
    </PfStepFrame>
  );
}

// Step 3 the first kudos landing, reactions piling on
function PfVisualLive({ p }) {
  const app = APPS.find(a => a.id === 'clappy');
  const Logo = app.Logo;
  return (
    <PfStepFrame>
      <div style={{
        width: 224, background: '#fff', borderRadius: 12,
        border: '1px solid var(--bh-line)', boxShadow: 'var(--bh-shadow-md)',
        padding: '11px 13px',
      }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ width: 26, height: 26, flexShrink: 0, borderRadius: 7, overflow: 'hidden', background: app.bg }}>
            <Logo />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--bh-ink)' }}>Clappy</span>
              <span style={{ fontSize: 7.5, padding: '1px 3px', background: 'rgba(31,27,22,.08)', color: 'var(--bh-ink-mute)', borderRadius: 3, fontWeight: 700 }}>APP</span>
              <span style={{ fontSize: 9, color: 'var(--bh-ink-mute)' }}>just now</span>
            </div>
            <div style={{
              marginTop: 4, borderLeft: `3px solid ${app.color}`, background: app.bg,
              borderRadius: '0 7px 7px 0', padding: '7px 9px',
            }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--bh-ink)' }}>🎉 First kudos: Maya → Jordan</div>
              <div style={{ fontSize: 9.5, color: 'var(--bh-ink-soft)', marginTop: 2, lineHeight: 1.4 }}>“Welcome aboard, BuddiesHR!”</div>
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 5, alignItems: 'center' }}>
              {[['🙌', 12], ['❤️', 8], ['🎉', 5]].map(([e, n]) => (
                <span key={e} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9.5, padding: '2px 6px', background: 'var(--bh-cream)', border: '1px solid rgba(31,27,22,.1)', borderRadius: 10, color: 'var(--bh-ink)', fontWeight: 600 }}>
                  <span>{e}</span><span>{n}</span>
                </span>
              ))}
              <span style={{ fontSize: 9, color: 'var(--bh-ink-mute)', fontWeight: 600 }}>+24 piling on…</span>
            </div>
          </div>
        </div>
      </div>
    </PfStepFrame>
  );
}

const PF_STEP_VISUALS = [PfVisualInstall, PfVisualChannels, PfVisualLive];

// ═══ INSTALL STEPS ═══
export function PlatformInstall({ p }) {
  return (
    <section className="bh-section" data-screen-label="Install steps" style={{ maxWidth: 1240, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', maxWidth: 700, margin: '0 auto 52px' }}>
        <div className="bh-chip" style={{ marginBottom: 16 }}>⏱️ Live before your coffee gets cold</div>
        <h2 style={{ fontSize: 'clamp(32px, 4.4vw, 54px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}>
          From the {p.id === 'slack' ? 'Slack' : 'Teams'} store<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>to your first kudos.</span>
        </h2>
        <p style={{ fontSize: 17, color: 'var(--bh-ink-mute)', marginTop: 16, lineHeight: 1.55 }}>
          No implementation project, no CSM, no four-week rollout plan. Here is the entire setup:
        </p>
      </div>
      <div className="pf-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
        {p.steps.map((s, i) => {
          const Visual = PF_STEP_VISUALS[i];
          return (
          <div key={s.num} style={{
            background: 'var(--bh-surface)', borderRadius: 'var(--bh-r-lg)',
            border: '1.5px solid var(--bh-line)', padding: 28,
            boxShadow: 'var(--bh-shadow-sm)', position: 'relative',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: 'var(--bh-sun-300)', border: '1.5px solid var(--bh-ink)',
                boxShadow: '0 3px 0 var(--bh-ink)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--bh-font-display)', fontSize: 20, fontWeight: 700, color: 'var(--bh-ink)',
              }}>{s.num}</div>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--bh-sun-600)', background: 'var(--bh-sun-50)', border: '1px solid var(--bh-sun-200)', padding: '4px 10px', borderRadius: 999 }}>{s.time}</span>
            </div>
            <Visual p={p} />
            <h3 style={{ fontSize: 21, fontWeight: 600, letterSpacing: '-0.02em', marginBottom: 8 }}>{s.title}</h3>
            <p style={{ fontSize: 15, color: 'var(--bh-ink-soft)', lineHeight: 1.55 }}>{s.body}</p>
          </div>
          );
        })}
      </div>
    </section>
  );
}

// ═══ FEATURE VISUALS mini UI vignettes per native surface ═══
function PfVKudos({ p }) {
  const app = APPS.find(a => a.id === 'clappy');
  const Logo = app.Logo;
  return (
    <PfStepFrame>
      <div style={{ width: 230, background: '#fff', borderRadius: 12, border: '1px solid var(--bh-line)', boxShadow: 'var(--bh-shadow-sm)', overflow: 'hidden' }}>
        <div style={{ padding: '5px 10px', borderBottom: '1px solid var(--bh-line)', fontSize: 9.5, fontWeight: 700, color: 'var(--bh-ink-mute)' }}>
          {p.id === 'slack' ? '# team-wins' : 'Team wins'} · 58 members
        </div>
        <div style={{ padding: '8px 10px', display: 'flex', gap: 7 }}>
          <div style={{ width: 24, height: 24, flexShrink: 0, borderRadius: 6, overflow: 'hidden', background: app.bg }}><Logo /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: 'var(--bh-ink-soft)' }}><b>Maya</b> → <b>Jordan</b></div>
            <div style={{ marginTop: 3, borderLeft: `3px solid ${app.color}`, background: app.bg, borderRadius: '0 7px 7px 0', padding: '6px 8px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--bh-ink)' }}>🎉 Saved the launch · #craft</div>
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
              {[['🙌', 12], ['❤️', 8], ['🎯', 3]].map(([e, n]) => (
                <span key={e} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9, padding: '1px 6px', background: 'var(--bh-cream)', border: '1px solid rgba(31,27,22,.1)', borderRadius: 10, color: 'var(--bh-ink)', fontWeight: 600 }}>{e} {n}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PfStepFrame>
  );
}

function PfVPulse({ p }) {
  const app = APPS.find(a => a.id === 'pulsy');
  const Logo = app.Logo;
  return (
    <PfStepFrame>
      <div style={{ width: 216, background: '#fff', borderRadius: 12, border: '1px solid var(--bh-line)', boxShadow: 'var(--bh-shadow-sm)', padding: '10px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, overflow: 'hidden', background: app.bg }}><Logo /></div>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--bh-ink)' }}>Pulsy</span>
          <span style={{ fontSize: 8.5, color: 'var(--bh-ink-mute)', marginLeft: 'auto', fontWeight: 600 }}>{p.id === 'slack' ? 'Direct message' : 'Private chat'}</span>
        </div>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--bh-ink)' }}>How’s your workload this week?</div>
        <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
          {[['😌 Light', false], ['🙂 Right', true], ['😮‍💨 Heavy', false]].map(([label, on]) => (
            <span key={label} style={{
              fontSize: 9.5, fontWeight: 600, padding: '4px 8px', borderRadius: 8,
              background: on ? app.bg : 'var(--bh-cream)',
              border: '1px solid', borderColor: on ? app.color : 'var(--bh-line)',
              color: 'var(--bh-ink)', whiteSpace: 'nowrap',
            }}>{label}</span>
          ))}
        </div>
        <div style={{ fontSize: 8.5, color: 'var(--bh-ink-mute)', marginTop: 7, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Anonymous · 1 question · 1 minute</div>
      </div>
    </PfStepFrame>
  );
}

function PfVSlash() {
  return (
    <PfStepFrame>
      <div style={{ width: 222, display: 'flex', flexDirection: 'column', gap: 7 }}>
        <div style={{ background: '#fff', border: '1.5px solid var(--bh-line-strong)', borderRadius: 9, padding: '8px 10px', fontSize: 10.5, color: 'var(--bh-ink)', display: 'flex', alignItems: 'center', gap: 2 }}>
          <span style={{ fontFamily: 'var(--bh-font-mono)' }}>/give</span>
          <span style={{ width: 1.5, height: 12, background: 'var(--bh-ink)', marginLeft: 1 }}></span>
        </div>
        <div style={{ background: '#fff', border: '1px solid var(--bh-line)', borderRadius: 10, boxShadow: 'var(--bh-shadow-sm)', overflow: 'hidden' }}>
          <div style={{ padding: '7px 10px', display: 'flex', alignItems: 'center', gap: 7, background: 'var(--bh-sun-50)', borderBottom: '1px solid var(--bh-line)' }}>
            <span style={{ fontSize: 11 }}>👏</span>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--bh-ink)' }}>Give recognition</span>
          </div>
          <div style={{ padding: '9px 10px', display: 'flex', flexDirection: 'column', gap: 7 }}>
            <div>
              <div style={{ fontSize: 8.5, fontWeight: 700, color: 'var(--bh-ink-mute)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3 }}>To</div>
              <div style={{ border: '1px solid var(--bh-line)', borderRadius: 7, padding: '5px 7px', fontSize: 10, color: 'var(--bh-ink)', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 13, height: 13, borderRadius: 4, background: 'var(--bh-coral-400)' }}></span>@maya
              </div>
            </div>
            <div>
              <div style={{ fontSize: 8.5, fontWeight: 700, color: 'var(--bh-ink-mute)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3 }}>Value</div>
              <div style={{ display: 'flex', gap: 5 }}>
                <span style={{ fontSize: 9.5, fontWeight: 600, color: 'var(--bh-ink)', background: 'var(--bh-sun-50)', border: '1px solid var(--bh-line)', borderRadius: 999, padding: '3px 8px' }}>#craft</span>
                <span style={{ fontSize: 9.5, color: 'var(--bh-ink-mute)', border: '1px solid var(--bh-line)', borderRadius: 999, padding: '3px 8px' }}>#team</span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 8.5, fontWeight: 700, color: 'var(--bh-ink-mute)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3 }}>Message</div>
              <div style={{ border: '1px solid var(--bh-line)', borderRadius: 7, padding: '5px 7px', fontSize: 10, color: 'var(--bh-ink)' }}>shipped it 🚀</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: 'var(--bh-ink)', borderRadius: 7, padding: '5px 12px' }}>Give kudos</span>
            </div>
          </div>
        </div>
      </div>
    </PfStepFrame>
  );
}

function PfVStats() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, padding: '9px 10px' }}>
      {[['Points', '320'], ['Pairings', '2'], ['Next 🎂', '3d']].map(([k, v]) => (
        <div key={k} style={{ background: 'var(--bh-cream)', border: '1px solid var(--bh-line)', borderRadius: 8, padding: '6px 7px' }}>
          <div style={{ fontSize: 8, color: 'var(--bh-ink-mute)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>{k}</div>
          <div style={{ fontFamily: 'var(--bh-font-display)', fontSize: 15, fontWeight: 700, color: 'var(--bh-ink)', letterSpacing: '-0.02em', marginTop: 1 }}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function PfVAppHome() {
  const app = APPS.find(a => a.id === 'clappy');
  const Logo = app.Logo;
  return (
    <PfStepFrame>
      <div style={{ width: 224, background: '#fff', borderRadius: 12, border: '1px solid var(--bh-line)', boxShadow: 'var(--bh-shadow-sm)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 10px 0' }}>
          <div style={{ width: 20, height: 20, borderRadius: 5, overflow: 'hidden', background: app.bg }}><Logo /></div>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--bh-ink)' }}>Clappy</span>
        </div>
        <div style={{ display: 'flex', gap: 12, padding: '6px 10px 0', borderBottom: '1px solid var(--bh-line)', fontSize: 9.5, fontWeight: 600 }}>
          <span style={{ color: 'var(--bh-ink)', borderBottom: '2px solid var(--bh-ink)', paddingBottom: 4 }}>Home</span>
          <span style={{ color: 'var(--bh-ink-mute)', paddingBottom: 4 }}>Messages</span>
          <span style={{ color: 'var(--bh-ink-mute)', paddingBottom: 4 }}>About</span>
        </div>
        <PfVStats />
      </div>
    </PfStepFrame>
  );
}

function PfVTab() {
  return (
    <PfStepFrame>
      <div style={{ width: 224, background: '#fff', borderRadius: 12, border: '1px solid var(--bh-line)', boxShadow: 'var(--bh-shadow-sm)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 12, padding: '8px 10px 0', borderBottom: '1px solid var(--bh-line)', fontSize: 9.5, fontWeight: 600 }}>
          <span style={{ color: 'var(--bh-ink-mute)', paddingBottom: 5 }}>Posts</span>
          <span style={{ color: 'var(--bh-ink-mute)', paddingBottom: 5 }}>Files</span>
          <span style={{ color: '#5B5FC7', borderBottom: '2px solid #5B5FC7', paddingBottom: 5, fontWeight: 700 }}>BuddiesHR</span>
          <span style={{ color: 'var(--bh-ink-mute)', paddingBottom: 5 }}>+</span>
        </div>
        <PfVStats />
      </div>
    </PfStepFrame>
  );
}

function PfVBilly({ p }) {
  const app = APPS.find(a => a.id === 'billy');
  const Logo = app.Logo;
  return (
    <PfStepFrame>
      <div style={{ width: 230, background: '#fff', borderRadius: 12, border: '1px solid var(--bh-line)', boxShadow: 'var(--bh-shadow-sm)', overflow: 'hidden' }}>
        <div style={{ padding: '5px 10px', borderBottom: '1px solid var(--bh-line)', fontSize: 9.5, fontWeight: 700, color: 'var(--bh-ink-mute)' }}>
          {p.id === 'slack' ? '# celebrations' : 'Celebrations'} · 58 members
        </div>
        <div style={{ padding: '8px 10px', display: 'flex', gap: 7 }}>
          <div style={{ width: 24, height: 24, flexShrink: 0, borderRadius: 6, overflow: 'hidden', background: app.bg }}><Logo /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: 'var(--bh-ink-soft)' }}><b>Billy</b> · today</div>
            <div style={{ marginTop: 3, borderLeft: `3px solid ${app.color}`, background: app.bg, borderRadius: '0 7px 7px 0', padding: '6px 8px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--bh-ink)' }}>🎂 It’s Priya’s birthday!</div>
              <div style={{ fontSize: 9, color: 'var(--bh-ink-soft)', marginTop: 1 }}>Drop a note and make her day.</div>
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
              {[['🎉', 14], ['🥳', 9], ['🎈', 5]].map(([e, n]) => (
                <span key={e} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9, padding: '1px 6px', background: 'var(--bh-cream)', border: '1px solid rgba(31,27,22,.1)', borderRadius: 10, color: 'var(--bh-ink)', fontWeight: 600 }}>{e} {n}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PfStepFrame>
  );
}

function PfVPalmy({ p }) {
  const app = APPS.find(a => a.id === 'palmy');
  const Logo = app.Logo;
  return (
    <PfStepFrame>
      <div style={{ width: 230, background: '#fff', borderRadius: 12, border: '1px solid var(--bh-line)', boxShadow: 'var(--bh-shadow-sm)', overflow: 'hidden' }}>
        <div style={{ padding: '5px 10px', borderBottom: '1px solid var(--bh-line)', fontSize: 9.5, fontWeight: 700, color: 'var(--bh-ink-mute)' }}>
          {p.id === 'slack' ? '@palmy' : 'Palmy'} · time off
        </div>
        <div style={{ padding: '8px 10px', display: 'flex', gap: 7 }}>
          <div style={{ width: 24, height: 24, flexShrink: 0, borderRadius: 6, overflow: 'hidden', background: app.bg }}><Logo /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: 'var(--bh-ink-soft)' }}><b>Palmy</b> · just now</div>
            <div style={{ marginTop: 3, borderLeft: `3px solid ${app.color}`, background: app.bg, borderRadius: '0 7px 7px 0', padding: '6px 8px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--bh-ink)' }}>🌴 Time-off request</div>
              <div style={{ fontSize: 9, color: 'var(--bh-ink-soft)', marginTop: 1 }}>Aug 12, Aug 16 · 5 days</div>
              <div style={{ display: 'flex', gap: 4, marginTop: 5 }}>
                <span style={{ fontSize: 9, padding: '2px 8px', background: 'var(--app-clappy)', color: '#fff', borderRadius: 6, fontWeight: 700 }}>Approve</span>
                <span style={{ fontSize: 9, padding: '2px 8px', background: 'var(--bh-cream)', border: '1px solid rgba(31,27,22,.12)', borderRadius: 6, fontWeight: 700, color: 'var(--bh-ink-soft)' }}>Deny</span>
              </div>
            </div>
            <div style={{ marginTop: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bh-cream)', border: '1px solid var(--bh-line)', borderRadius: 7, padding: '5px 8px' }}>
              <span style={{ fontSize: 8.5, color: 'var(--bh-ink-mute)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>Balance left</span>
              <span style={{ fontFamily: 'var(--bh-font-display)', fontSize: 13, fontWeight: 700, color: 'var(--bh-ink)' }}>14 days</span>
            </div>
          </div>
        </div>
      </div>
    </PfStepFrame>
  );
}

function PfVDashLink() {
  return (
    <PfStepFrame>
      <div style={{ width: 222, background: '#fff', borderRadius: 12, border: '1px solid var(--bh-line)', boxShadow: 'var(--bh-shadow-sm)', padding: '11px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
          <span style={{ fontSize: 14 }}>🔗</span>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--bh-ink)' }}>Your BuddiesHR dashboard</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 9 }}>
          {[['Points', '320'], ['Pairings', '2'], ['Next 🎂', '3d']].map(([k, v]) => (
            <div key={k} style={{ background: 'var(--bh-cream)', border: '1px solid var(--bh-line)', borderRadius: 8, padding: '6px 7px' }}>
              <div style={{ fontSize: 8, color: 'var(--bh-ink-mute)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>{k}</div>
              <div style={{ fontFamily: 'var(--bh-font-display)', fontSize: 14, fontWeight: 700, color: 'var(--bh-ink)', letterSpacing: '-0.02em', marginTop: 1 }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 10, fontWeight: 700, color: '#fff', background: '#5B5FC7', borderRadius: 8, padding: '7px 10px' }}>
          Open dashboard ↗
        </div>
      </div>
    </PfStepFrame>
  );
}

function PfVCoffee({ p }) {
  const app = APPS.find(a => a.id === 'alfy');
  const Logo = app.Logo;
  return (
    <PfStepFrame>
      <div style={{ width: 224, background: '#fff', borderRadius: 12, border: '1px solid var(--bh-line)', boxShadow: 'var(--bh-shadow-sm)', padding: '9px 11px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
          <div style={{ display: 'flex' }}>
            {[['MK', '#FFB020'], ['PS', '#4A6FA5']].map(([ini, c], i) => (
              <span key={ini} style={{ width: 18, height: 18, borderRadius: '50%', background: c, color: '#fff', fontSize: 7.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid #fff', marginLeft: i === 0 ? 0 : -6 }}>{ini}</span>
            ))}
            <span style={{ width: 18, height: 18, borderRadius: '50%', overflow: 'hidden', background: app.bg, border: '1.5px solid #fff', marginLeft: -6 }}><Logo /></span>
          </div>
          <span style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--bh-ink)' }}>Maya, Priya, Alfy</span>
          <span style={{ fontSize: 8.5, color: 'var(--bh-ink-mute)', marginLeft: 'auto', fontWeight: 600 }}>{p.id === 'slack' ? 'Group DM' : 'Group chat'}</span>
        </div>
        <div style={{ borderLeft: `3px solid ${app.color}`, background: app.bg, borderRadius: '0 7px 7px 0', padding: '6px 9px' }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--bh-ink)' }}>☕️ You two are paired this week!</div>
          <div style={{ fontSize: 9.5, color: 'var(--bh-ink-soft)', marginTop: 2 }}>How about Friday 11:00?</div>
        </div>
        <div style={{ display: 'flex', gap: 5, marginTop: 7 }}>
          <span style={{ fontSize: 9.5, fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: 'var(--bh-ink)', color: 'var(--bh-cream)' }}>Works 👍</span>
          <span style={{ fontSize: 9.5, fontWeight: 600, padding: '4px 10px', borderRadius: 999, background: 'var(--bh-cream)', border: '1px solid var(--bh-line-strong)', color: 'var(--bh-ink-soft)' }}>Another time</span>
        </div>
      </div>
    </PfStepFrame>
  );
}

function PfVQuiet() {
  return (
    <PfStepFrame>
      <div style={{ width: 212, background: '#fff', borderRadius: 12, border: '1px solid var(--bh-line)', boxShadow: 'var(--bh-shadow-sm)', padding: '11px 13px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15 }}>🌙</span>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--bh-ink)', flex: 1 }}>Quiet hours</span>
          <span style={{ width: 30, height: 17, borderRadius: 999, background: 'var(--app-clappy)', position: 'relative', flexShrink: 0 }}>
            <span style={{ position: 'absolute', top: 2, right: 2, width: 13, height: 13, borderRadius: '50%', background: '#fff' }}></span>
          </span>
        </div>
        <div style={{ fontSize: 9.5, color: 'var(--bh-ink-mute)', marginTop: 5 }}>Paused · resumes 9:00 AM, everyone’s local time</div>
        <div style={{ display: 'flex', gap: 5, marginTop: 9, flexWrap: 'wrap' }}>
          {['SF · 1:12 AM 💤', 'Paris · 10:12 AM', 'Tokyo · 5:12 PM'].map(tz => (
            <span key={tz} style={{ fontSize: 8.5, fontWeight: 600, padding: '3px 8px', borderRadius: 999, background: 'var(--bh-cream)', border: '1px solid var(--bh-line)', color: 'var(--bh-ink-soft)', whiteSpace: 'nowrap' }}>{tz}</span>
          ))}
        </div>
      </div>
    </PfStepFrame>
  );
}

function PfVMobile() {
  const app = APPS.find(a => a.id === 'billy');
  const Logo = app.Logo;
  return (
    <PfStepFrame>
      <div style={{ width: 104, background: '#fff', border: '3px solid var(--bh-ink)', borderRadius: 18, padding: '8px 7px 10px', boxShadow: 'var(--bh-shadow-md)' }}>
        <div style={{ width: 34, height: 4, borderRadius: 999, background: 'var(--bh-line-strong)', margin: '0 auto 8px' }}></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5 }}>
          <div style={{ width: 14, height: 14, borderRadius: 4, overflow: 'hidden', background: app.bg }}><Logo /></div>
          <span style={{ fontSize: 7.5, fontWeight: 700, color: 'var(--bh-ink)' }}>Billy</span>
          <span style={{ fontSize: 6.5, color: 'var(--bh-ink-mute)', marginLeft: 'auto' }}>now</span>
        </div>
        <div style={{ borderLeft: `2px solid ${app.color}`, background: app.bg, borderRadius: '0 5px 5px 0', padding: '4px 6px' }}>
          <div style={{ fontSize: 7.5, fontWeight: 700, color: 'var(--bh-ink)', lineHeight: 1.3 }}>🎂 It’s Priya’s birthday!</div>
        </div>
        <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
          {['🎉 4', '🥳 2'].map(r => (
            <span key={r} style={{ fontSize: 6.5, padding: '1px 4px', background: 'var(--bh-cream)', border: '1px solid rgba(31,27,22,.1)', borderRadius: 6, fontWeight: 600, color: 'var(--bh-ink)' }}>{r}</span>
          ))}
        </div>
      </div>
    </PfStepFrame>
  );
}

function PfVSSO() {
  return (
    <PfStepFrame>
      <div style={{ width: 212, background: '#fff', borderRadius: 12, border: '1px solid var(--bh-line)', boxShadow: 'var(--bh-shadow-sm)', padding: '11px 13px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--app-pulsy)', color: '#fff', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>MK</span>
          <div>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--bh-ink)' }}>Maya Kapoor</div>
            <div style={{ fontSize: 9.5, color: 'var(--bh-ink-mute)' }}>maya@acme.com</div>
          </div>
        </div>
        <div style={{ marginTop: 9, paddingTop: 9, borderTop: '1px dashed var(--bh-line)', display: 'grid', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 9.5, color: 'var(--bh-ink-soft)', fontWeight: 600 }}>
            <span style={{ width: 13, height: 13, borderRadius: '50%', background: 'var(--app-clappy)', color: '#fff', fontSize: 8, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✓</span>
            Signed in with Microsoft 365
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 9.5, color: 'var(--bh-ink-soft)', fontWeight: 600 }}>
            <span style={{ width: 13, height: 13, borderRadius: '50%', background: 'var(--app-clappy)', color: '#fff', fontSize: 8, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✓</span>
            No new account, no password
          </div>
        </div>
      </div>
    </PfStepFrame>
  );
}

const PF_FEATURE_VISUALS = { kudos: PfVKudos, pulse: PfVPulse, slash: PfVSlash, apphome: PfVAppHome, tab: PfVTab, billy: PfVBilly, palmy: PfVPalmy, dashlink: PfVDashLink, coffee: PfVCoffee, quiet: PfVQuiet, mobile: PfVMobile, sso: PfVSSO };

// ═══ NATIVE FEATURES GRID ═══
export function PlatformFeatures({ p }) {
  return (
    <section className="bh-section" data-screen-label={`Native in ${p.name}`} style={{ background: 'var(--bh-cream-deep)', borderTop: '1px solid var(--bh-line)', borderBottom: '1px solid var(--bh-line)' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 52px' }}>
          <div className="bh-chip" style={{ marginBottom: 16 }}>
            <img src={pfAsset(p.logo)} alt="" style={{ width: 15, height: 15 }} />
            Native, not bolted on
          </div>
          <h2 style={{ fontSize: 'clamp(32px, 4.4vw, 54px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}>
            {p.id === 'slack' ? (
              <>
                Your team never has to<br/>
                <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>leave <img src={pfAsset(p.logo)} alt="" style={{ height: '0.74em', width: 'auto', verticalAlign: '-0.05em', margin: '0 0.08em' }} /> {p.name}.</span>
              </>
            ) : (
              <>
                Built for how your team<br/>
                <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>already uses <img src={pfAsset(p.logo)} alt="" style={{ height: '0.74em', width: 'auto', verticalAlign: '-0.05em', margin: '0 0.08em' }} /> {p.name}.</span>
              </>
            )}
          </h2>
          <p style={{ fontSize: 17, color: 'var(--bh-ink-mute)', marginTop: 16, lineHeight: 1.55 }}>
            Most HR tools send a notification with a link to their portal. BuddiesHR does the whole thing asking, answering, celebrating inside {p.name}.
          </p>
          {p.id === 'teams' && (
            <div className="bh-chip" style={{ marginTop: 18 }}>
              🎂 Billy and ☕️ Alfy are live on Teams today. The rest of the suite is rolling out.
            </div>
          )}
        </div>
        <div className="pf-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
          {p.features.map((f) => {
            const Visual = PF_FEATURE_VISUALS[f.visual];
            return (
            <div key={f.title} style={{
              background: 'var(--bh-surface)', borderRadius: 'var(--bh-r-lg)',
              border: '1.5px solid var(--bh-line)', padding: 26,
              boxShadow: 'var(--bh-shadow-sm)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 26, opacity: f.soon ? 0.55 : 1 }}>{f.emoji}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {f.soon && (
                    <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--bh-ink)', background: 'var(--bh-sun-50)', border: '1px solid var(--bh-sun-400)', padding: '4px 10px', borderRadius: 999 }}>Coming soon</span>
                  )}
                  <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--bh-ink-mute)', background: 'var(--bh-cream)', border: '1px solid var(--bh-line)', padding: '4px 10px', borderRadius: 999 }}>{f.surface}</span>
                </div>
              </div>
              <div style={{ opacity: f.soon ? 0.5 : 1, filter: f.soon ? 'saturate(0.7)' : 'none' }}>
                {Visual && <Visual p={p} />}
              </div>
              <h3 style={{ fontSize: 19, fontWeight: 600, letterSpacing: '-0.015em', marginBottom: 7, color: f.soon ? 'var(--bh-ink-soft)' : 'var(--bh-ink)' }}>{f.title}</h3>
              <p style={{ fontSize: 14.5, color: 'var(--bh-ink-soft)', lineHeight: 1.55 }}>{f.body}</p>
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ═══ FAQ ═══
export function PlatformFAQ({ p }) {
  const [open, setOpen] = React.useState(0);
  return (
    <section className="bh-section" data-screen-label="FAQ" style={{ maxWidth: 880, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 44 }}>
        <div className="bh-chip" style={{ marginBottom: 16 }}>🙋 Asked by every {p.id === 'slack' ? 'Slack admin' : 'IT admin'}</div>
        <h2 style={{ fontSize: 'clamp(30px, 4vw, 48px)', letterSpacing: '-0.03em', lineHeight: 1.08 }}>
          The questions that come up<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>before the install.</span>
        </h2>
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        {p.faq.map((item, i) => {
          const isOpen = open === i;
          return (
            <div key={item.q} style={{
              background: 'var(--bh-surface)', borderRadius: 'var(--bh-r-md)',
              border: '1.5px solid', borderColor: isOpen ? 'var(--bh-sun-400)' : 'var(--bh-line)',
              overflow: 'hidden', transition: 'border-color .2s',
            }}>
              <button onClick={() => setOpen(isOpen ? -1 : i)} style={{
                width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
                padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
                fontFamily: 'var(--bh-font-body)', fontSize: 16.5, fontWeight: 600, color: 'var(--bh-ink)',
              }}>
                {item.q}
                <span style={{
                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                  background: isOpen ? 'var(--bh-sun-300)' : 'var(--bh-cream)',
                  border: '1px solid', borderColor: isOpen ? 'var(--bh-ink)' : 'var(--bh-line-strong)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 700, color: 'var(--bh-ink)',
                  transform: isOpen ? 'rotate(45deg)' : 'none', transition: 'transform .2s, background .2s',
                }}>+</span>
              </button>
              {isOpen && (
                <div style={{ padding: '0 22px 20px', fontSize: 15, color: 'var(--bh-ink-soft)', lineHeight: 1.6, maxWidth: 700 }}>
                  {item.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ═══ CROSS-LINK TO THE OTHER PLATFORM ═══
export function PlatformSwitch({ p }) {
  const other = PLATFORMS[p.otherId];
  return (
    <section className="bh-section" data-screen-label="Other platform" style={{ paddingTop: 0, maxWidth: 1080, margin: '0 auto' }}>
      <Link href={p.otherHref} className="pf-switch" style={{
        display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 22, alignItems: 'center',
        background: 'var(--bh-surface)', borderRadius: 'var(--bh-r-lg)',
        border: '1.5px solid var(--bh-line)', boxShadow: 'var(--bh-shadow-sm)',
        padding: '26px 30px', textDecoration: 'none', color: 'var(--bh-ink)',
        transition: 'border-color .15s, transform .15s',
      }}
      onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--bh-sun-400)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--bh-line)'; e.currentTarget.style.transform = 'none'; }}
      >
        <img src={pfAsset(other.logo)} alt={other.name} style={{ width: 44, height: 44 }} />
        <div>
          <div style={{ fontFamily: 'var(--bh-font-display)', fontSize: 21, fontWeight: 600, letterSpacing: '-0.015em' }}>
            Your company runs on {other.name}?
          </div>
          <div style={{ fontSize: 14.5, color: 'var(--bh-ink-mute)', marginTop: 4 }}>
            BuddiesHR is just as native there same apps, same pricing, same 60-second start.
          </div>
        </div>
        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--bh-coral-400)', whiteSpace: 'nowrap' }}>
          BuddiesHR for {other.name} <span className="bh-arrow">→</span>
        </span>
      </Link>
    </section>
  );
}
